/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import InputMask from 'react-input-mask';
import _omit from 'lodash/omit';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import FilePondSingleUpload from 'uploader/FilePondSingleUpload';
import ImagePlaceholder from 'assets/img/image-placeholder.jpg';
import draftToHtml from 'draftjs-to-html';
import convertHtmlToDraft from 'html-to-draftjs';
import _isEmpty from 'lodash/isEmpty';
import Uploader from '../components/Uploader';
import '../../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

const parseNumber = (val) => {
  try {
    return parseFloat(val);
  } catch (err) {
    return NaN;
  }
};

const count = (s1, letter) => s1.split(letter).length - 1;

export default (WrappedComponent) =>
  function WithForm(props) {
    const [form, setForm] = useState({});

    const handleEditorStateChange = (key) => (editorState) => {
      setForm({ ...form, [key]: editorState });
    };

    const handleChangeValue = (key) => (value) => {
      setForm({ ...form, [key]: value });
    };

    const handleChangeInput =
      (key) =>
      ({ target }) => {
        setForm({ ...form, [key]: target.value });
      };

    const handleLowerCase =
      (key) =>
      ({ target }) => {
        setForm({ ...form, [key]: target.value });
      };

    const handleChangeCheck =
      (key) =>
      ({ target }) => {
        setForm({ ...form, [key]: target.checked });
      };

    const handleChangeInputNumber =
      (key) =>
      ({ target }) => {
        const svalue = target.value;
        const value = parseNumber(svalue);
        if (
          (Number.isNaN(value) && !_isEmpty(value)) ||
          count(`${svalue}`, '.') > 1
        )
          return;
        setForm({ ...form, [key]: svalue.replace(/[^0-9\.]/g, '') }); // eslint-disable-line
      };

    const renderLabel = (x, p) => (
      <label className="form-label d-flex align-items-center m-0 mr-1">
        <span>
          {x.label}{' '}
          {p.required && !x.isReadOnly && (
            <span className="text-danger">
              <i>Required</i>
            </span>
          )}
        </span>
        {x.note && (
          <small className="ml-auto mr-0 text-muted">
            <i>{x.note}</i>
          </small>
        )}
      </label>
    );

    const setFormData = (payload) => {
      setForm({ ...form, ...payload });
    };

    const resetFormData = (defaultValue = {}) => {
      setForm(defaultValue);
    };

    const createRichTextEditor = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        <Editor
          initialEditorState={EditorState.createEmpty()}
          onEditorStateChange={handleEditorStateChange(data.key)}
          editorState={form[data.key]}
          {...p}
          toolbar={{
            colorPicker: {
              colors: [
                'rgb(97,189,109)',
                'rgb(26,188,156)',
                'rgb(84,172,210)',
                'rgb(44,130,201)',
                'rgb(147,101,184)',
                'rgb(71,85,119)',
                'rgb(204,204,204)',
                'rgb(65,168,95)',
                'rgb(0,168,133)',
                'rgb(61,142,185)',
                'rgb(41,105,176)',
                'rgb(85,57,130)',
                'rgb(40,50,78)',
                'rgb(0,0,0)',
                'rgb(247,218,100)',
                'rgb(251,160,38)',
                'rgb(235,107,86)',
                'rgb(226,80,65)',
                'rgb(163,143,132)',
                'rgb(239,239,239)',
                'rgb(255,255,255)',
                'rgb(250,197,28)',
                'rgb(243,121,52)',
                'rgb(209,72,65)',
                'rgb(184,49,47)',
                'rgb(124,112,107)',
                'rgb(209,213,216)',
                'rgb(134,168,46)',
              ],
            },
          }}
        />
      </React.Fragment>
    );

    const htmlToDraft = (data) => {
      const { contentBlocks, entityMap } = convertHtmlToDraft(data || '');

      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );
      const editorState = EditorState.createWithContent(contentState);

      return editorState;
    };

    const createInput = (data, p = {}, className = '') => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className={`${className} form-control-plaintext`}
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <input
            className={`${className} form-control`}
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createHidden = (data, p = {}) => (
      <input
        type="hidden"
        className="form-control"
        onChange={handleChangeInput(data.key)}
        value={form[data.key] || ''}
        {...p}
      />
    );

    const createLowerCaseInput = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <input
            className="form-control"
            onChange={handleLowerCase(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createUpperCaseInput = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <input
            className="form-control-uppercase"
            onChange={handleLowerCase(data.key)}
            value={form[data.key] || ''}
            autoFocus
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createCheckbox = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            type="checkbox"
            readOnly
            checked={form[data.key] || false}
            {...p}
          />
        ) : (
          <input
            className="form-control"
            type="checkbox"
            onChange={handleChangeCheck(data.key)}
            checked={form[data.key] || false}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createTextArea = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <p style={{ minHeight: 80 }}>{form[data.key]}</p>
        ) : (
          <textarea
            className="form-control"
            rows="3"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
            style={{ resize: 'none' }}
          />
        )}
      </React.Fragment>
    );

    const createInputNumber = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <input
            className="form-control"
            type="number"
            onChange={handleChangeInputNumber(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createInputMask = (data, mask, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask={mask}
            maskChar=""
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createInputMobileNumber = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask="999999999999"
            alwaysShowMask
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createTelephoneNumber = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask="999-999-9999"
            alwaysShowMask
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createTinNumber = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask="999-999-999-999"
            alwaysShowMask
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const inputPin = (data, p = {}) => (
      <React.Fragment>
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask="999"
            alwaysShowMask
            className="form-control text-center number-spacing"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createInputGroupNumber = (
      data,
      p = {},
      inputPlaceholder = '\u20B1'
    ) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text font-weight-bold">
                {inputPlaceholder}
              </span>
            </div>
            <input
              readOnly
              value={form[data.key] || ''}
              className="form-control"
              {...p}
            />
          </div>
        ) : (
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text font-weight-bold">
                {inputPlaceholder}
              </span>
            </div>
            <InputMask
              mask="9999999999"
              maskChar=""
              placeholder="0.00"
              className="form-control"
              onChange={handleChangeInput(data.key)}
              value={form[data.key] || ''}
              {...p}
            />
          </div>
        )}
      </React.Fragment>
    );

    const createSelect = (data, options, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control"
            readOnly
            value={
              (options.find((x) => x.value === form[data.key] || '') || {})
                .label || ''
            }
            {...p}
          />
        ) : (
          <select
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {..._omit(p, ['isLoading'])}
          >
            <option value="">
              {p.isLoading ? 'Loading...' : p.placeholder || '- Select -'}
            </option>
            {options.map((item, i) => (
              <option
                key={`${item.value}-${i}`} // eslint-disable-line
                value={item.value}
                disabled={item.disabled}
              >
                {item.label}
              </option>
            ))}
          </select>
        )}
      </React.Fragment>
    );

    const createUploader = (data, obj) => (
      <React.Fragment>
        <div className="d-flex align-items-center justify-between">
          {renderLabel(data, obj)}
        </div>
        {data?.sub && (
          <small className="d-block mb-2 font-weight-bold text-danger">
            {data?.sub}
          </small>
        )}
        <img
          className="w-100 rounded"
          src={form[data.key] || ImagePlaceholder}
          alt="form[data.key]"
        />
        <FilePondSingleUpload
          value={form[data.key] || ''}
          onChange={handleChangeValue(data.key)}
          required={obj.required}
          acceptedFileTypes={obj?.acceptedFileTypes}
        />
      </React.Fragment>
    );

    const createReadOnly = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        <p className="form-control-plaintext" {...p}>
          {form[data.key] || '-'}
        </p>
      </React.Fragment>
    );

    return (
      <WrappedComponent
        {...props}
        formData={form}
        setFormData={setFormData}
        resetFormData={resetFormData}
        createInput={createInput}
        createRichTextEditor={createRichTextEditor}
        createLowerCaseInput={createLowerCaseInput}
        createUpperCaseInput={createUpperCaseInput}
        createCheckbox={createCheckbox}
        createTextArea={createTextArea}
        createInputNumber={createInputNumber}
        createSelect={createSelect}
        createReadOnly={createReadOnly}
        createInputMask={createInputMask}
        createInputMobileNumber={createInputMobileNumber}
        createUploader={createUploader}
        createInputGroupNumber={createInputGroupNumber}
        createTelephoneNumber={createTelephoneNumber}
        createTinNumber={createTinNumber}
        inputPin={inputPin}
        createHidden={createHidden}
        draftToHtml={draftToHtml}
        htmlToDraft={htmlToDraft}
        convertToRaw={convertToRaw}
      />
    );
  };
